import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { useParams } from 'react-router-dom';
import {
    Modal,
    ModalHeader,
    ModalBody,
    ModalFooterConfirmDismiss,
    Label,
    SingleSelect,
    VerticalFlex,
    Input,
    HorizontalFlex,
    PadContainer,
    ButtonWithIcon,
    icons,
} from 'smartsuite-ui';
import ConfirmModal from './ConfirmModal';
import { useLabelContext } from '../../shared/hooks/labelContext';
import { getKeyFromSavedSearchName } from '../searchUtils';
import {
    isFavoriteDisabled,
    saveFavorite,
} from '../../shared/filtering/favoriteUtils';

const SAVED_DASHBOARD_MAX_LENGTH = 50;

/* eslint-disable max-statements, complexity */
export default function SaveAsModal(props) {
    const { searchKey } = useParams();
    const { dashboardLabels, translationLabels } = useLabelContext();

    const [isFavorite, setIsFavorite] = useState(false);
    const [isConfirmModalVisible, setIsConfirmModalVisible] = useState(false);
    const [activeSaveOption, setActiveSaveOption] = useState('saveNew');
    const [saveNewName, setSaveNewName] = useState('');
    const [overwriteName, setOverwriteName] = useState({
        name: '',
        key: '',
    });
    const [isDuplicateName, setIsDuplicateName] = useState(false);

    useEffect(() => {
        setIsFavorite(false);
        setActiveSaveOption(
            props.modifiedFrom && props.modifiedFrom !== 'Default'
                ? 'overwriteExisting'
                : 'saveNew'
        );
    }, [props.isModalVisible, props.modifiedFrom]);

    useEffect(() => {
        if (
            props.isModalVisible &&
            props.visibleOverwriteOptions.length &&
            activeSaveOption === 'overwriteExisting' &&
            props.modifiedFrom
        ) {
            const foundOverwrite = props.visibleOverwriteOptions.find(
                (option) => option.key === props.modifiedFrom
            );

            if (foundOverwrite) {
                setOverwriteName(foundOverwrite);
            }
        }
    }, [
        props.isModalVisible,
        props.visibleOverwriteOptions,
        activeSaveOption,
        props.modifiedFrom,
    ]);

    const onSaveNewInputChange = (event) => {
        const inputName = event.target.value;
        const isDuplicate = !!props.visibleOverwriteOptions.find(
            (option) =>
                option.name.toLowerCase() === inputName.toLowerCase().trim()
        );

        setIsDuplicateName(isDuplicate);
        setSaveNewName(inputName);
    };

    const onSelectChange = (event) => setOverwriteName(event);

    const onDismissConfirm = (name) => () => {
        setSaveNewName(name);
        setIsConfirmModalVisible(false);
    };

    /* eslint-disable react/jsx-one-expression-per-line */
    const onSave = () => {
        const isSaveNew = activeSaveOption === 'saveNew';
        const name = isSaveNew ? saveNewName : overwriteName.key;
        const favoriteName = `${props.pageTitle}: ${
            isSaveNew ? saveNewName : overwriteName.name
        }`;
        setIsConfirmModalVisible(false);
        setSaveNewName('');
        props.onConfirm(name, isSaveNew);

        if (isFavorite) {
            // prettier-ignore
            const key = isSaveNew
                ? `${searchKey}_${getKeyFromSavedSearchName(name)}`.toLowerCase()
                : name;

            saveFavorite(
                props.siteMapKey,
                favoriteName,
                'Save Search',
                key,
                null,
                isSaveNew ? saveNewName : overwriteName.name
            );
        }
    };

    if (isConfirmModalVisible) {
        const confirmTitle =
            isDuplicateName && activeSaveOption === 'saveNew'
                ? dashboardLabels.savedNameAlreadyExists
                : translationLabels.confirm;

        const overwriteMessage =
            isDuplicateName && activeSaveOption === 'saveNew'
                ? saveNewName
                : overwriteName.name;

        return (
            <ConfirmModal
                confirmTitle={confirmTitle}
                confirmLabel={translationLabels.yes}
                dismissLabel={translationLabels.no}
                onConfirm={onSave}
                onDismiss={onDismissConfirm(saveNewName)}
            >
                <Label>
                    {`${dashboardLabels.areYouSureYouWantTo} ${dashboardLabels.overwrite} `}
                    <strong>{`"${overwriteMessage}"`}</strong>?
                </Label>
            </ConfirmModal>
        );
    }
    /* eslint-enable react/jsx-one-expression-per-line */

    const onDismiss = () => {
        setActiveSaveOption('saveNew');
        setSaveNewName('');
        props.onDismiss();
    };

    return (
        props.isModalVisible && (
            <Modal onOverlayClick={onDismiss}>
                <ModalHeader onDismiss={onDismiss}>
                    {dashboardLabels.saveFilters}
                </ModalHeader>
                <ModalBody>
                    <PadContainer>
                        <VerticalFlex>
                            <HorizontalFlex
                                cssClassModifiers={['align-flex-end']}
                            >
                                <input
                                    type="radio"
                                    name="savedDashboardOptions"
                                    id="saveNew"
                                    className="save-as-modal__radio-button"
                                    checked={activeSaveOption === 'saveNew'}
                                    onChange={() =>
                                        setActiveSaveOption('saveNew')
                                    }
                                />
                                <VerticalFlex
                                    cssClassModifiers={['small-relative']}
                                >
                                    <Label>{dashboardLabels.saveAsNew}</Label>
                                    <Input
                                        type="text"
                                        value={saveNewName}
                                        onChange={onSaveNewInputChange}
                                        onFocus={() =>
                                            setActiveSaveOption('saveNew')
                                        }
                                        maxLength={SAVED_DASHBOARD_MAX_LENGTH}
                                    />
                                </VerticalFlex>
                            </HorizontalFlex>
                            <div className="save-as-modal__character-count">
                                <Label>{`${saveNewName.length}/${SAVED_DASHBOARD_MAX_LENGTH}`}</Label>
                            </div>
                            {!!props.visibleOverwriteOptions.length && (
                                <HorizontalFlex
                                    cssClassModifiers={['align-flex-end']}
                                >
                                    <input
                                        type="radio"
                                        name="savedDashboardOptions"
                                        id="overwriteExisting"
                                        className="save-as-modal__radio-button"
                                        checked={
                                            activeSaveOption ===
                                            'overwriteExisting'
                                        }
                                        onChange={() =>
                                            setActiveSaveOption(
                                                'overwriteExisting'
                                            )
                                        }
                                    />

                                    <VerticalFlex
                                        cssClassModifiers={['small-relative']}
                                    >
                                        <Label>
                                            {dashboardLabels.overwriteExisting}
                                        </Label>
                                        <div className="save-as-modal__overwrite-container">
                                            <SingleSelect
                                                options={
                                                    props.visibleOverwriteOptions
                                                }
                                                keyName="key"
                                                textField="name"
                                                valueField="key"
                                                onChange={onSelectChange}
                                                hasBlankOption={false}
                                                onFocus={() =>
                                                    setActiveSaveOption(
                                                        'overwriteExisting'
                                                    )
                                                }
                                                value={overwriteName}
                                            />
                                        </div>
                                    </VerticalFlex>
                                </HorizontalFlex>
                            )}
                            {!isFavoriteDisabled && props.siteMapKey && (
                                <>
                                    <PadContainer
                                        cssClassModifiers={['pad-medium']}
                                    />
                                    <HorizontalFlex>
                                        <div className="save-as-modal__add-to-favorites-icon">
                                            <ButtonWithIcon
                                                iconClass={
                                                    isFavorite
                                                        ? icons.favoriteFilled
                                                        : icons.favorite
                                                }
                                                onClick={() =>
                                                    setIsFavorite(!isFavorite)
                                                }
                                            />
                                        </div>
                                        <Label>
                                            {dashboardLabels.addToFavorites}
                                        </Label>
                                    </HorizontalFlex>
                                </>
                            )}
                        </VerticalFlex>
                    </PadContainer>
                </ModalBody>
                <ModalFooterConfirmDismiss
                    confirmLabel={translationLabels.save}
                    dismissLabel={translationLabels.cancel}
                    onConfirm={
                        activeSaveOption === 'saveNew' && !isDuplicateName
                            ? () => onSave()
                            : () => setIsConfirmModalVisible(true)
                    }
                    onDismiss={onDismiss}
                    isConfirmDisabled={
                        activeSaveOption === 'saveNew' &&
                        (!saveNewName ||
                            saveNewName === dashboardLabels.default)
                    }
                />
            </Modal>
        )
    );
}
/* eslint-enable */

SaveAsModal.propTypes = {
    isModalVisible: PropTypes.bool.isRequired,
    onConfirm: PropTypes.func.isRequired,
    onDismiss: PropTypes.func.isRequired,
    modifiedFrom: PropTypes.string,
    pageTitle: PropTypes.string.isRequired,
    siteMapKey: PropTypes.string,
    visibleOverwriteOptions: PropTypes.instanceOf(Array),
};

SaveAsModal.defaultProps = {
    visibleOverwriteOptions: [],
    modifiedFrom: '',
};
